import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import {
  useAdmin,
  useAuthStore,
  useIsAdminAuthenticated,
  useIsAuthenticated,
} from '@/stores/auth-store';
import { ROUTES } from '@/lib/constants';
import { routeForUser } from '@/features/auth/hooks';
import { adminApiClient } from '@/features/admin/api';
import { canAccessAdminArea, type AdminAreaAccess } from '@/features/admin/access';
import { PageSpinner } from '@/components/ui/spinner';

/** Sends unauthenticated visitors to /login while preserving the intended target. */
export function ProtectedRoute() {
  const authed = useIsAuthenticated();
  const hydrated = useAuthStore((s) => s.hydrated);
  const location = useLocation();

  if (!hydrated) return <PageSpinner />;
  if (!authed) {
    return <Navigate to={ROUTES.login} replace state={{ from: location.pathname }} />;
  }
  return <Outlet />;
}

/** Public-only routes (login/signup) that bounce already-authed users to their home. */
export function PublicOnlyRoute() {
  const authed = useIsAuthenticated();
  const user = useAuthStore((s) => s.user);
  const hydrated = useAuthStore((s) => s.hydrated);
  const { pathname } = useLocation();

  if (!hydrated) return <PageSpinner />;
  if (authed) {
    // Finishing signup sets the session while still on /signup, so this guard
    // re-renders before the signup page's own navigate(welcome) lands. A user who
    // is authed on /signup with onboarding still ahead has just signed up: show
    // the welcome page once (it continues to their onboarding route).
    if (pathname === ROUTES.signup && user && !user.profile_complete) {
      return <Navigate to={ROUTES.welcome} replace />;
    }
    // Honour role *and* onboarding state: a freshly-signed-up employer goes to
    // employer onboarding, a new candidate to profile completion, completed
    // users to their home. Mirrors the post-login routing in routeForUser().
    return <Navigate to={user ? routeForUser(user) : ROUTES.dashboard} replace />;
  }
  return <Outlet />;
}

/**
 * Restricts a route to a single user role.
 *
 * A mismatch bounces to the user's *own* home — an employer sent to the
 * candidate dashboard would just bounce again, since that route is
 * candidate-only too.
 */
export function RoleRoute({ role }: { role: 'candidate' | 'employer' }) {
  const user = useAuthStore((s) => s.user);
  const hydrated = useAuthStore((s) => s.hydrated);
  if (!hydrated) return <PageSpinner />;
  if (!user) return <Navigate to={ROUTES.login} replace />;
  const userRole = user.role === 'job_seeker' ? 'candidate' : user.role;
  if (userRole !== role) {
    return <Navigate to={routeForUser(user)} replace />;
  }
  return <Outlet />;
}

export function AdminProtectedRoute() {
  const authed = useIsAdminAuthenticated();
  const hydrated = useAuthStore((s) => s.hydrated);
  const setAdmin = useAuthStore((s) => s.setAdmin);

  // Refresh the stored admin (incl. module_access / is_superuser) on entry so
  // nav and route gating reflect current server-side access — without forcing a
  // re-login when modules were granted after the session started.
  const meQuery = useQuery({
    queryKey: ['admin', 'me'],
    queryFn: adminApiClient.me,
    enabled: hydrated && authed,
    staleTime: 60_000,
    retry: false,
  });
  useEffect(() => {
    if (meQuery.data) setAdmin(meQuery.data);
  }, [meQuery.data, setAdmin]);

  if (!hydrated) return <PageSpinner />;
  if (!authed) return <Navigate to={ROUTES.adminLogin} replace />;
  return <Outlet />;
}

/**
 * Restricts an admin console area to admins holding its module (or super
 * admins), the same rule the sidebar uses. Typing the URL of a hidden area
 * bounces to the console overview instead of rendering the page.
 */
export function AdminAccessRoute(access: AdminAreaAccess) {
  const admin = useAdmin();
  if (!canAccessAdminArea(admin, access)) return <Navigate to={ROUTES.admin} replace />;
  return <Outlet />;
}
